import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Divider,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Spinner,
  Text,
} from '@chakra-ui/react';
import {
  CloseCircleOutline as CloseCircle,
  resolveRecognitionValueDisplay,
} from '@aslanpay/portal-common-lib';
import { useManagingCompanyStore } from 'app/store';
import useRecognitionDashboard from 'app/hooks/useRecognitionDashboard';

import { LeaderboardMetric } from '../LeaderboardTabs';
import LeaderboardRow from '../LeaderboardRow';

const FETCH_LIMIT = 500;
const PAGE_SIZE = 20;

export type LeaderboardKind = 'values' | 'recognised' | 'engaged' | 'groups' | 'pots';

interface LeaderboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  kind: LeaderboardKind;
  cardTitle: string;
  metric: LeaderboardMetric;
}

interface NormalisedEntry {
  id: string;
  primary: string;
  secondary?: string;
  count: number;
  points?: number;
}

function useLeaderboardEntries(
  kind: LeaderboardKind,
  companyId: string,
  enabled: boolean
): { entries: NormalisedEntry[]; isLoading: boolean } {
  const values = useRecognitionDashboard.useFetchValuesLeaderboard(
    companyId,
    FETCH_LIMIT,
    enabled && kind === 'values'
  );
  const recognised = useRecognitionDashboard.useFetchMostRecognisedEmployees(
    companyId,
    FETCH_LIMIT,
    enabled && kind === 'recognised'
  );
  const engaged = useRecognitionDashboard.useFetchMostEngagedEmployees(
    companyId,
    FETCH_LIMIT,
    enabled && kind === 'engaged'
  );
  const groups = useRecognitionDashboard.useFetchGroupsLeaderboard(
    companyId,
    FETCH_LIMIT,
    enabled && kind === 'groups'
  );
  const pots = useRecognitionDashboard.useFetchPotsLeaderboard(
    companyId,
    FETCH_LIMIT,
    enabled && kind === 'pots'
  );

  return useMemo(() => {
    if (kind === 'values') {
      const entries: NormalisedEntry[] =
        values.data?.entries.map((e) => {
          const { name, emoji } = resolveRecognitionValueDisplay(e.valueName, e.valueEmoji);
          return {
            id: e.valueId,
            primary: `${emoji} ${name.trim()}`,
            count: e.count,
          };
        }) ?? [];
      return { entries, isLoading: values.isLoading };
    }
    if (kind === 'recognised' || kind === 'engaged') {
      const query = kind === 'recognised' ? recognised : engaged;
      const entries: NormalisedEntry[] =
        query.data?.entries.map((e) => ({
          id: e.individualId,
          primary: `${e.firstName} ${e.lastName}`,
          secondary: e.jobTitle || undefined,
          count: e.count,
          points: e.points,
        })) ?? [];
      return { entries, isLoading: query.isLoading };
    }
    const query = kind === 'groups' ? groups : pots;
    const entries: NormalisedEntry[] =
      query.data?.entries.map((e) => ({
        id: e.groupId,
        primary: e.groupName,
        count: e.count,
        points: e.points,
      })) ?? [];
    return { entries, isLoading: query.isLoading };
  }, [kind, values, recognised, engaged, groups, pots]);
}

function LeaderboardModal({ isOpen, onClose, kind, cardTitle, metric }: LeaderboardModalProps) {
  const company = useManagingCompanyStore((state) => state.company);
  const companyId = company?.id ?? '';
  const { entries, isLoading } = useLeaderboardEntries(kind, companyId, isOpen);

  const sorted = useMemo(
    () => [...entries].sort((a, b) => (b[metric] ?? 0) - (a[metric] ?? 0)),
    [entries, metric]
  );

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    if (isOpen) setVisibleCount(PAGE_SIZE);
  }, [isOpen, metric, kind]);

  const sentinelRef = useRef<HTMLDivElement>(null);
  const handleObserve = useCallback(
    (observed: IntersectionObserverEntry[]) => {
      if (observed[0].isIntersecting && visibleCount < sorted.length) {
        setVisibleCount((c) => Math.min(c + PAGE_SIZE, sorted.length));
      }
    },
    [visibleCount, sorted.length]
  );

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver(handleObserve, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleObserve]);

  const visible = sorted.slice(0, visibleCount);
  const title =
    kind === 'values' ? cardTitle : `${cardTitle} by ${metric === 'count' ? 'count' : 'points'}`;

  const renderBody = () => {
    if (isLoading) {
      return (
        <Flex justify="center" py="40px">
          <Spinner color="brand.purple" />
        </Flex>
      );
    }
    if (sorted.length === 0) {
      return (
        <Flex flexDir="column" align="center" justify="center" py="40px" gap="8px">
          <Text textStyle="h5Medium" color="brand.darkPurple">
            Things are just getting started
          </Text>
          <Text textStyle="small" color="brand.darkGray" textAlign="center">
            Recognition insights will appear here once people begin sending recognitions.
          </Text>
        </Flex>
      );
    }
    return (
      <Flex flexDir="column">
        {visible.map((entry, index) => (
          <Box key={entry.id}>
            {index > 0 && <Divider borderColor="brand.lightGray1" />}
            <LeaderboardRow
              rank={index + 1}
              primary={entry.primary}
              secondary={entry.secondary}
              value={metric === 'count' ? entry.count : entry.points ?? 0}
            />
          </Box>
        ))}
        <Box ref={sentinelRef} h="1px" />
        {visibleCount < sorted.length && (
          <Flex justify="center" py="12px">
            <Spinner size="sm" color="brand.purple" />
          </Flex>
        )}
      </Flex>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent w={{ md: '480px' }} p="24px" position="relative">
        <ModalHeader p="0" mb="16px">
          <HStack>
            <Text as="h6" textStyle="h5SemiBold" color="brand.mutedPurple">
              {title}
            </Text>
            <Spacer />
            <IconButton
              variant="link"
              icon={<CloseCircle boxSize="30px" />}
              aria-label="Close modal"
              onClick={onClose}
            />
          </HStack>
        </ModalHeader>

        <ModalBody p="0" maxH="60vh">
          {renderBody()}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default LeaderboardModal;
